/**
 * How many of the level's parts the player has accounted for, in the objective corner
 * (design D4). A separate section from `ObjectiveHud` rather than a line inside it: the
 * objective list is the profile's authored text, and this is derived from the level's
 * contents — a level can have parts with no objective mentioning them, and the reverse.
 *
 * Fed the level's built objects as a prop, the way `StowButton` is: the store deliberately
 * holds only ids (`carriedObjectId`, `pocket`, `attachments`), never a copy of the level's
 * contents, and the tally needs each part's kind to tell a part from anything else.
 *
 * `role="status"` with `aria-live="polite"`, the same as the objective list: a count that
 * changes when the player picks something up is worth announcing, never worth interrupting.
 */

import { tallyParts } from '../game/assembly';
import type { PlacedObject } from '../game/profile/buildGeometry';
import { useGameStore } from '../store/gameStore';

// "Dog parts" is fixed text for now (design D5): deriving it from the host kind's label
// would be a lookup with one entry today and a sentence to compose for a second assembly
// that does not exist yet.
const LABEL = 'Dog parts';

export function PartTallyHud({ objects }: { objects: readonly PlacedObject[] }) {
  const carriedObjectId = useGameStore((store) => store.carriedObjectId);
  const pocket = useGameStore((store) => store.pocket);
  const attachments = useGameStore((store) => store.attachments);

  const { found, total } = tallyParts(objects, { carriedObjectId, pocket, attachments });
  if (total === 0) return null;

  return (
    <section className="part-tally-hud" role="status" aria-live="polite">
      <span className="part-tally-hud__label">{LABEL}</span>{' '}
      <span className="part-tally-hud__count">
        {found} / {total}
      </span>
    </section>
  );
}
